import React, { useEffect, useState } from 'react';
import { Droplet, Recycle, Sparkles, Leaf } from 'lucide-react';
import { fetchAdminImpact } from '../../lib/api';

/**
 * Dharma Impact — the founder's "Proof of Dharma" view.
 *
 * Replaces the legacy sales charts with four organic visualizations of
 * VALUE: Water Stewardship (ripple), Plastic Burden Reduced (vessels grid),
 * Seva Velocity (botanical sap-flow), and Purity Integrity (lab dial).
 *
 * Palette: #C1D7E2 / #90B0BF / #7C9CB1 on #F4F8F9. No hard bar charts.
 */
const DharmaImpact = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchAdminImpact().then(setData).catch(() => setErr('Could not load impact report.'));
  }, []);

  if (err) return <p className="text-[#7C9CB1]">{err}</p>;
  if (!data) {
    return (
      <div data-testid="impact-loading" className="h-64 bg-[#C1D7E2]/40 animate-pulse" />
    );
  }

  return (
    <div data-testid="impact-view" className="space-y-12">
      {/* Header */}
      <header className="border-b border-[#C1D7E2] pb-8">
        <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF] mb-3">
          Dharma Impact · Proof of Dharma
        </p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-tight">
          We measure <em className="text-[#90B0BF]">value</em>, not just price.
        </h2>
        <p className="mt-4 font-serif italic text-[#90B0BF] max-w-2xl leading-relaxed">
          {data.policy}
        </p>
      </header>

      {/* Four impact panels */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <WaterStewardship data={data.water_stewardship} />
        <PlasticBurden data={data.plastic_burden} />
        <SevaVelocity data={data.seva_velocity} />
        <PurityIntegrity data={data.purity_integrity} />
      </section>
    </div>
  );
};

/* ====================  WATER STEWARDSHIP  ==================== */
const WaterStewardship = ({ data }) => (
  <Panel
    testId="impact-water"
    eyebrow="Water Stewardship"
    icon={Droplet}
    title={`${data.liters_sewer_positive.toLocaleString()} L`}
    subtitle="Sewer-positive water released"
    narrative={data.narrative}
  >
    {/* Ripple rings — concentric SVG circles that breathe */}
    <div className="relative h-56 flex items-center justify-center overflow-hidden">
      <svg viewBox="0 0 320 220" className="w-full h-full">
        <defs>
          <radialGradient id="ripple-fill" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C1D7E2" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#C1D7E2" stopOpacity="0" />
          </radialGradient>
        </defs>
        {[110, 88, 66, 44, 24].map((r, i) => (
          <circle
            key={r}
            cx="160" cy="110" r={r}
            fill="none"
            stroke="#7C9CB1"
            strokeOpacity={0.15 + i * 0.12}
            strokeWidth={i === 4 ? 1.2 : 0.6}
            style={{
              transformOrigin: '160px 110px',
              animation: `ripple 6s ease-in-out ${i * 0.6}s infinite`,
            }}
          />
        ))}
        <circle cx="160" cy="110" r="14" fill="url(#ripple-fill)" />
        <circle cx="160" cy="110" r="6" fill="#7C9CB1" />
      </svg>
      <style>{`
        @keyframes ripple {
          0%,100% { transform: scale(1); opacity: 0.45; }
          50%     { transform: scale(1.08); opacity: 0.85; }
        }
      `}</style>
    </div>
    <Stats
      rows={[
        ['Total formula in motion', `${data.ml_total.toLocaleString()} ml`],
        ['From orders',             `${data.ml_orders.toLocaleString()} ml`],
        ['From verified refills',   `${data.ml_refills.toLocaleString()} ml`],
        ['Biodegradability',        `${data.biodegradability_pct}%`],
      ]}
    />
  </Panel>
);

/* ====================  PLASTIC BURDEN  ==================== */
const PlasticBurden = ({ data }) => {
  // Render up to 60 vessel dots in a soft grid; over 60 we stack a counter.
  const dots = Math.min(data.permanent_glass_count, 60);
  return (
    <Panel
      testId="impact-plastic"
      eyebrow="Plastic Burden Reduced"
      icon={Recycle}
      title={`${(data.plastic_avoided_grams).toLocaleString()} g`}
      subtitle={`${data.plastic_avoided_kg} kg of single-use plastic avoided`}
      narrative={data.narrative}
    >
      <div
        data-testid="impact-vessel-grid"
        className="h-56 bg-[#F4F8F9] border border-[#C1D7E2] p-4 flex items-center justify-center"
      >
        <div className="grid grid-cols-12 gap-2">
          {Array.from({ length: dots }).map((_, i) => (
            <span
              key={i}
              className="w-3 h-5 rounded-t-full rounded-b-sm bg-[#90B0BF] opacity-70"
              style={{
                animation: `vesselFloat 5s ease-in-out ${(i * 0.07).toFixed(2)}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
      <style>{`
        @keyframes vesselFloat {
          0%,100% { transform: translateY(0); opacity: 0.6; }
          50%     { transform: translateY(-3px); opacity: 0.95; }
        }
      `}</style>
      <Stats
        rows={[
          ['Permanent glass vessels', data.permanent_glass_count],
          ['Verified refills',        data.refill_count],
          ['Per single-use bottle',   `${data.grams_per_bottle} g`],
        ]}
      />
    </Panel>
  );
};

/* ====================  SEVA VELOCITY  ==================== */
const SevaVelocity = ({ data }) => {
  const total = Math.max(data.total_in_motion, 1);
  const pct = (k) => Math.round((data.by_act[k] / total) * 100);
  const acts = [
    { key: 'refills',         label: 'Refills',         pctVal: pct('refills') },
    { key: 'content',         label: 'Content',         pctVal: pct('content') },
    { key: 'hotel_referrals', label: 'Hotel referrals', pctVal: pct('hotel_referrals') },
  ];

  return (
    <Panel
      testId="impact-seva"
      eyebrow="Seva Velocity"
      icon={Sparkles}
      title={`${data.total_in_motion.toLocaleString()}`}
      subtitle="SEVA in motion through service acts"
      narrative={data.narrative}
    >
      {/* Botanical sap-flow — three curved streams */}
      <div className="h-56 relative">
        <svg viewBox="0 0 320 240" className="w-full h-full">
          <defs>
            <linearGradient id="sap" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7C9CB1" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#C1D7E2" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          {/* central stem */}
          <path
            d="M160 200 Q 160 130 160 30"
            fill="none"
            stroke="#7C9CB1"
            strokeWidth="1.5"
          />
          {/* three branches sized by share */}
          {[
            { x: 50,  pctVal: acts[0].pctVal, label: acts[0].label, val: data.by_act.refills },
            { x: 160, pctVal: acts[1].pctVal, label: acts[1].label, val: data.by_act.content },
            { x: 270, pctVal: acts[2].pctVal, label: acts[2].label, val: data.by_act.hotel_referrals },
          ].map((b, i) => (
            <g key={i}>
              <path
                d={`M160 ${110 + i * 6} Q ${(160 + b.x) / 2} 150 ${b.x} ${180 - i * 4}`}
                fill="none"
                stroke="url(#sap)"
                strokeWidth={Math.max(2, b.pctVal / 5)}
                strokeLinecap="round"
              />
              <circle cx={b.x} cy={180 - i * 4} r="7" fill="#7C9CB1" />
              <text
                x={b.x}
                y={210}
                textAnchor="middle"
                fontSize="11"
                fontFamily="JetBrains Mono, monospace"
                fill="#7C9CB1"
                letterSpacing="1.5"
              >
                {b.label.toUpperCase()}
              </text>
              <text
                x={b.x}
                y={228}
                textAnchor="middle"
                fontSize="13"
                fontFamily="Cormorant Garamond, serif"
                fontStyle="italic"
                fill="#90B0BF"
              >
                {b.val.toLocaleString()} · {b.pctVal}%
              </text>
            </g>
          ))}
        </svg>
      </div>
      <style>{`
        @keyframes sapFlow {
          0%,100% { opacity: 0.85; }
          50%     { opacity: 1; }
        }
      `}</style>
      <Stats
        rows={[
          ['Verified refills',       data.act_counts.refills],
          ['Content creators',       data.act_counts.content_creators],
          ['Hotel partners',         data.act_counts.hotel_partners],
        ]}
      />
    </Panel>
  );
};

/* ====================  PURITY INTEGRITY  ==================== */
const PurityIntegrity = ({ data }) => {
  const purity = data.purity_average_pct || 0;
  // Convert 0–100 % to a SVG arc length on a 270° gauge
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const arcLen = (purity / 100) * (circumference * 0.75);

  return (
    <Panel
      testId="impact-purity"
      eyebrow="Purity Integrity"
      icon={Leaf}
      title={`${purity.toFixed(1)}%`}
      subtitle={`Average across ${data.total_batches} batches · ${data.certified_batches} certified`}
      narrative={
        data.total_batches > 0
          ? `Free alkali ${data.free_alkali_average_pct.toFixed(2)} % · pH ${data.ph_average.toFixed(1)} · ${data.microbial_average_cfu_per_g.toFixed(0)} CFU/g average — well within thresholds.`
          : 'Awaiting first lab certificates.'
      }
    >
      {/* Editorial dial — soft 270° arc */}
      <div className="h-56 relative flex items-center justify-center">
        <svg viewBox="0 0 220 220" className="w-full h-full -rotate-[135deg]">
          <circle
            cx="110" cy="110" r={radius}
            fill="none"
            stroke="#C1D7E2"
            strokeWidth="6"
            strokeDasharray={`${circumference * 0.75} ${circumference}`}
            strokeLinecap="round"
          />
          <circle
            cx="110" cy="110" r={radius}
            fill="none"
            stroke="#7C9CB1"
            strokeWidth="6"
            strokeDasharray={`${arcLen} ${circumference}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1.6s ease-out' }}
          />
        </svg>
        <div className="absolute text-center">
          <p className="font-serif text-5xl text-[#7C9CB1] font-light leading-none">
            {purity.toFixed(0)}
          </p>
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#90B0BF] mt-1 uppercase">
            % composite
          </p>
        </div>
      </div>
      <Stats
        rows={[
          ['Batches certified', `${data.certified_batches} / ${data.total_batches}`],
          ['Free alkali avg',   `${data.free_alkali_average_pct.toFixed(2)} %  (max ${data.thresholds.free_alkali_pct_max})`],
          ['pH average',        `${data.ph_average.toFixed(1)}  (range ${data.thresholds.ph_min}–${data.thresholds.ph_max})`],
          ['Microbial avg',     `${data.microbial_average_cfu_per_g.toFixed(0)} CFU/g  (cap ${data.thresholds.microbial_cfu_per_g_max})`],
        ]}
      />
    </Panel>
  );
};

/* ====================  SHARED PRIMITIVES  ==================== */
const Panel = ({ testId, eyebrow, icon: Icon, title, subtitle, narrative, children }) => (
  <article
    data-testid={testId}
    className="bg-[#F4F8F9] border border-[#C1D7E2] p-7 md:p-8 backdrop-blur-md"
  >
    <header className="flex items-start gap-3 mb-6">
      <Icon size={20} strokeWidth={1.2} className="text-[#7C9CB1] mt-1" />
      <div>
        <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF]">
          {eyebrow}
        </p>
        <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-none mt-1">
          {title}
        </h3>
        <p className="mt-2 font-serif italic text-[#90B0BF]">{subtitle}</p>
      </div>
    </header>
    {children}
    {narrative && (
      <p className="mt-5 font-serif italic text-[#7C9CB1]/85 leading-relaxed border-t border-[#C1D7E2] pt-4">
        {narrative}
      </p>
    )}
  </article>
);

const Stats = ({ rows }) => (
  <ul className="mt-5 space-y-2">
    {rows.map(([k, v]) => (
      <li
        key={k}
        className="flex items-baseline justify-between border-b border-[#C1D7E2]/50 pb-2 last:border-b-0"
      >
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
          {k}
        </span>
        <span className="font-mono text-sm text-[#7C9CB1]">{v}</span>
      </li>
    ))}
  </ul>
);

export default DharmaImpact;
